import { useQuery } from "convex/react";
import { api } from "@repo/backend/api";

import { routing } from "@/i18n/routing";
import {
  derivePricingStrings,
  EMPTY_PRICING_STRINGS,
  type PricingStrings,
} from "@/lib/pricing-values";

// Re-exported so the constant keeps its long-standing import path; the
// derivation itself now lives in `lib/pricing-values.ts` next to the server
// reader. Only this one name has callers — the types stay unexported there.
export { EMPTY_PRICING_VALUES } from "@/lib/pricing-values";

// The landing's supported locales come straight from the next-intl routing
// config so this list can never drift from the router (#1013 review
// feedback — a hardcoded copy here would silently fall back to en-US when
// a third locale is added to `routing.ts` but not mirrored here).
const SUPPORTED_LOCALES: readonly string[] = routing.locales;

/**
 * Read the live pricing catalog (cents + currency for the sold credit pack)
 * and pre-render its derived strings for the current landing locale.
 *
 * Used by every landing surface that needs to show the same number the
 * Stripe checkout will charge, so "app shows $5, checkout shows R$ 25,00"
 * can never happen again (issue #1012). The hook returns `loading: true`
 * with empty strings until the catalog resolves — consumers render a single
 * dash / empty placeholder instead of a stale hardcoded value.
 *
 * Server components read the same catalog through `getServerPricingStrings`
 * in `lib/pricing-catalog-server.ts`; both share `derivePricingStrings`, so
 * the formatting can only be changed for both at once.
 *
 * The pack carries one price per currency Stripe charges; the locale picks
 * which one is shown (USD on en-US, BRL on pt-br) inside `derivePricingStrings`.
 */
// Locale warnings are deduplicated across renders with a module-level Set;
// the comment above usePricingCatalog falsely promised "once per session"
// without actually enforcing it. Dedupe here so a misconfigured call site
// doesn't flood the dev console (review fix on #1013).
const WARNED_LOCALES = new Set<string>();

export function usePricingCatalog(localeInput: string): PricingStrings {
  const supported = SUPPORTED_LOCALES.includes(localeInput);
  // Skipped in production so customer consoles stay clean.
  if (
    !supported &&
    process.env.NODE_ENV !== "production" &&
    !WARNED_LOCALES.has(localeInput)
  ) {
    WARNED_LOCALES.add(localeInput);
    console.warn(
      `[usePricingCatalog] unsupported locale "${localeInput}" — falling back to "${routing.defaultLocale}". Add it to \`locales\` in apps/web/i18n/routing.ts.`,
    );
  }
  const locale = supported ? localeInput : routing.defaultLocale;
  const catalog = useQuery(api.billing.catalog.getPricingCatalog);
  if (!catalog) {
    return EMPTY_PRICING_STRINGS;
  }
  return derivePricingStrings(catalog, locale);
}
